import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import { CcRequirementStatus, CcRequirementStatusCreate } from "server/models"
import { useInput, StateType } from "client/utils"
import {
    StoreType,
    CcRequirementStatusValidation,
    SaveCcRequirementStatus,
    closeModal,
    setCcRequirementStatusEdit,
    setCcRequirementStatusReloadList,
    setCcRequirementStatusReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    SelectDropdown,
    SelectDropdownItem,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

interface StatusEditProps {
    status: CcRequirementStatus
    editAccess: boolean
}

let isMounted: boolean

const Form = ({
    status,
    editAccess,
}: StatusEditProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(status.name)
    const [description, setDescription] = useInput(status.description)
    const [isDefault, setDefault] = useState(status.default)
    const [validation, setValidation] = useState<CcRequirementStatusValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (modals.length && isMounted) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newStatusData: CcRequirementStatusCreate = {
            accountId: status.accountId || userData.accountId,
            projectId: status.projectId || project.id,
            name,
            description,
            creator: status.id ? status.creator : userData.userId,
            default: isDefault,
            order: status.id ? status.order : 0,
        }

        SaveCcRequirementStatus(status.id, newStatusData, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setCcRequirementStatusEdit({
                ...newStatusData,
                id: res.newStatusId || status.id,
                order: res.newStatusOrder || status.order,
            }))

            dispatch(setCcRequirementStatusReloadList(false))
            setTimeout(() => {
                dispatch(setCcRequirementStatusReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setCcRequirementStatusReloadEdit(false))
                setTimeout(() => {
                    dispatch(setCcRequirementStatusReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} readOnly={!editAccess} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={description} onChange={setDescription} readOnly={!editAccess} />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.default}</Label>
                        {
                            editAccess && (
                                <SelectDropdown title={isDefault ? lang.yes : lang.no}>
                                    <SelectDropdownItem onClick={() => setDefault(true)}>{lang.yes}</SelectDropdownItem>
                                    <SelectDropdownItem onClick={() => setDefault(false)}>{lang.no}</SelectDropdownItem>
                                </SelectDropdown>
                            )
                        }
                        {
                            !editAccess && (
                                <Input type="text" value={isDefault ? lang.yes : lang.no} readOnly />
                            )
                        }
                        <Feedback>{validation && validation.default}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    {
                        editAccess && [
                            <Button key="save" variant="success" row onClick={() => onSave(1)}>
                                {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                            </Button>,
                            <Button key="apply" variant="success" row onClick={() => onSave(2)}>
                                {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                            </Button>,
                        ]
                    }
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
